export interface DeezerTrack {
  id: number;
  title: string;
  artist: string;
  albumCover: string;
  previewUrl: string;
}

export interface LrcLine {
  time: number;
  text: string;
}

const INVIDIOUS_INSTANCES = [
  'https://invidious.fdn.fr',
  'https://invidious.nerdvpn.de',
  'https://yewtu.be',
];

// ─── LOCAL CACHE ────────────────────────────────────────────────────────────

const SEARCH_CACHE_TTL = 7 * 24 * 3600 * 1000; // 7 days
const SEARCH_PREFIX = 'velora_dz_q_';
const SAVED_KEY = 'velora_saved_songs';

function _cacheKey(query: string) {
  return SEARCH_PREFIX + query.toLowerCase().trim();
}

function _readSearchCache(query: string): DeezerTrack[] | null {
  try {
    const raw = localStorage.getItem(_cacheKey(query));
    if (!raw) return null;
    const { tracks, cachedAt } = JSON.parse(raw) as { tracks: DeezerTrack[]; cachedAt: number };
    if (Date.now() - cachedAt > SEARCH_CACHE_TTL) {
      localStorage.removeItem(_cacheKey(query));
      return null;
    }
    return tracks;
  } catch {
    return null;
  }
}

function _writeSearchCache(query: string, tracks: DeezerTrack[]) {
  try {
    localStorage.setItem(_cacheKey(query), JSON.stringify({ tracks, cachedAt: Date.now() }));
  } catch {}
}

// ─── SAVED SONGS ─────────────────────────────────────────────────────────────

export function getSavedSongs(): DeezerTrack[] {
  try {
    return JSON.parse(localStorage.getItem(SAVED_KEY) ?? '[]') as DeezerTrack[];
  } catch {
    return [];
  }
}

export function saveSong(track: DeezerTrack) {
  const list = getSavedSongs().filter(s => s.id !== track.id);
  list.unshift(track);
  if (list.length > 100) list.splice(100);
  try { localStorage.setItem(SAVED_KEY, JSON.stringify(list)); } catch {}
}

export function removeSavedSong(id: number) {
  const list = getSavedSongs().filter(s => s.id !== id);
  try { localStorage.setItem(SAVED_KEY, JSON.stringify(list)); } catch {}
}

export function isSongSaved(id: number): boolean {
  return getSavedSongs().some(s => s.id === id);
}

// ─── ITUNES SEARCH ───────────────────────────────────────────────────────────

export async function searchDeezer(query: string): Promise<DeezerTrack[]> {
  const cached = _readSearchCache(query);
  if (cached) return cached;

  try {
    const r = await fetch(
      `https://itunes.apple.com/search?term=${encodeURIComponent(query)}&entity=song&limit=15&media=music`
    );
    if (!r.ok) return [];
    const d = await r.json();
    const tracks: DeezerTrack[] = (d.results ?? []).map((t: any) => ({
      id: t.trackId as number,
      title: t.trackName ?? '',
      artist: t.artistName ?? '',
      albumCover: t.artworkUrl100?.replace('100x100bb', '300x300bb') ?? '',
      previewUrl: t.previewUrl ?? '',
    }));
    _writeSearchCache(query, tracks);
    return tracks;
  } catch {
    return [];
  }
}

// ─── INVIDIOUS VIDEO ID ───────────────────────────────────────────────────────

export async function findVideoId(artist: string, title: string): Promise<string | null> {
  const q = encodeURIComponent(`${artist} ${title}`);
  const attempts = INVIDIOUS_INSTANCES.map(base =>
    fetch(`${base}/api/v1/search?q=${q}&type=video&fields=videoId,title&sort_by=relevance`)
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then((items: any[]) => {
        const id = items[0]?.videoId as string | undefined;
        if (!id) throw new Error('no result');
        return id;
      })
  );
  return Promise.any(attempts).catch(() => null);
}

// ─── LRCLIB LYRICS ───────────────────────────────────────────────────────────

export async function getLyrics(artist: string, title: string): Promise<LrcLine[]> {
  try {
    const r = await fetch(
      `https://lrclib.net/api/search?artist_name=${encodeURIComponent(artist)}&track_name=${encodeURIComponent(title)}`
    );
    if (!r.ok) return [];
    const items: any[] = await r.json();
    const hit = items.find(i => i.syncedLyrics);
    if (!hit?.syncedLyrics) return [];
    return parseLrc(hit.syncedLyrics as string);
  } catch {
    return [];
  }
}

function parseLrc(lrc: string): LrcLine[] {
  const out: LrcLine[] = [];
  for (const line of lrc.split('\n')) {
    const m = line.match(/^\[(\d{2}):(\d{2})\.(\d{2,3})\](.*)$/);
    if (!m) continue;
    const dec = m[3].length === 3 ? 1000 : 100;
    out.push({ time: +m[1] * 60 + +m[2] + +m[3] / dec, text: m[4].trim() });
  }
  return out.sort((a, b) => a.time - b.time);
}
